import { Component, OnInit, Inject, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ElementTypes, Stripe, StripeCardNumberElementChangeEvent, StripeHtmlElementsRegistry, PaymentMethod } from '@stripe/stripe-js';

import * as State from '@shared/state';
import * as Tokens from '@shared/core/tokens';
import * as Utils from '@shared/core/utils';
import { StripePaymentProviderMapper } from '@shared/core/mappers/paymentProviders/stripe.payment-provider.shared.mapper';
import { Controllers } from '@shared/core/controllers';

import { FormTypesComponent } from '../form-types.shared.component';

import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
    selector: 'shared-form-for-payment-method-from-provider',
    templateUrl: './form-for-payment-method-from-provider.shared.component.html',
    styleUrls: ['./form-for-payment-method-from-provider.shared.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormForPaymentMethodFromProviderComponent extends FormTypesComponent implements OnInit, OnDestroy, AfterViewInit {
    private _staticLoader: OLO.Components.LOADING_STATUS = null;
    private _stripe: Stripe;
    private _stripeFieldsRegistry: StripeHtmlElementsRegistry;
    private _isValid: boolean = false;
    private _adyenEncryptedPaymentData: Adyen.PaymentData = null;
    @ViewChild('formWrapper', { static: false }) public container!: ElementRef<HTMLDivElement>;

    public hint = {
        feel: 'danger',
        size: 'sm',
        vside: 'center',
        hside: 'right',
    };

    public fieldsStatus: Record<Adyen.FieldInputTypes, OLO.Components.IMappedMessage> = {
        encryptedCardNumber: null,
        encryptedExpiryDate: null,
        encryptedSecurityCode: null
    };

    public touched: Record<Adyen.FieldInputTypes, boolean> = {
        encryptedCardNumber: false,
        encryptedExpiryDate: false,
        encryptedSecurityCode: false
    };

    public adyenConfigState$: Observable<State.AdyenLocationConfig>;
    public stripeConfigState$: Observable<State.StripeLocationConfig>;
    public isMakingRequests$: Observable<OLO.Components.LOADING_STATUS>;

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) public config: IConfig,
        public formBuilder: FormBuilder,
        public controllers: Controllers,
        public changeDetectorRef: ChangeDetectorRef,
    ) {
        super(config, formBuilder);
        this.formName = 'payment-method-from-provider';

        this.compact = false;
        this.labelSubmit = 'Add payment method';
    }

    protected _initProviders(): void {
        setTimeout(() => {
            if(this.isAdyen) this._initAdyen();
            if(this.isStripe) this._initStripe();
        }, 0);
    }

    protected _initAdyen(): void {
        const target = this.container?.nativeElement || `#${this.formName}`;

        this.controllers.payment.initAdyenForm(target, {
            onChange: (state, reactComponent) => {
                this._isValid = state.isValid;

                this._adyenEncryptedPaymentData = Object.keys(state.data.paymentMethod).reduce((paymentData, key) => {
                    if(key === 'type') {
                        return paymentData;
                    }

                    return {
                        ...paymentData,
                        [key]: state.data.paymentMethod[key]
                    };
                }, {} as Adyen.PaymentData);

                this.changeDetectorRef.detectChanges();
            },
            onBinValue: ({ binValue }) => {

            },
            onError: (errorObj) => {
                this._updateFieldStatus(errorObj.fieldType, errorObj.error && {
                    key: errorObj.i18n,
                    message: errorObj.i18n,
                    messageType: 'danger',
                } || null);
            },
            onFieldValid:(validObj) => {
                if(validObj.fieldType === 'encryptedCardNumber') {
                    this.formControl.controls.cardNumber.setValue(validObj.endDigits);
                }

                this._updateFieldStatus(validObj.fieldType, null);
            }
        });
    }

    protected _initStripe(): void {
        this.controllers.payment.initStripeForm({
            card: '[data-cse="encryptedCardNumber"]',
            // cardExpiry: '[data-cse="encryptedExpiryDate"]',
            // cardCvc: '[data-cse="encryptedSecurityCode"]',
        }, {
            card: {}
        }).then(({ registry, stripe }) => {
            this._stripe = stripe;
            this._stripeFieldsRegistry = registry;

            const handler = (field: ElementTypes) => (e: StripeCardNumberElementChangeEvent) => {
                const elemType = e.elementType as ElementTypes;
                let mappedField: Adyen.FieldInputTypes = 'encryptedCardNumber';
                if(elemType === 'cardExpiry') {
                    mappedField = 'encryptedExpiryDate';
                }
                if(elemType === 'cardCvc') {
                    mappedField = 'encryptedSecurityCode';
                }

                this._updateFieldStatus(mappedField, e.error && {
                    key: e.error.message,
                    message: e.error.message,
                    messageType: 'danger'
                } || null);

                // let isValid = true;
                // for(const checkField in this.fieldsStatus) {
                //     if(isValid) {
                //         isValid = this.fieldsStatus[checkField] === null && this.touched[checkField] === true;
                //     }
                // }

                this._isValid = e.complete;

                this.changeDetectorRef.detectChanges();
            };

            for(const field in registry) {
                registry[field].on('change', handler(field as ElementTypes));
            }

        });
    }

    protected _setTouched(fieldType: Adyen.FieldInputTypes): void {
        this.touched[fieldType] = true;
    }

    protected _updateFieldStatus(fieldType: Adyen.FieldInputTypes, error: OLO.Components.IMappedMessage): OLO.Components.IMappedMessage {
        this._setTouched(fieldType);

        for(const key in this.fieldsStatus) {
            if(key !== fieldType) {
                this.fieldsStatus[key] = null;
            }
        }

        this.fieldsStatus[fieldType] = error;
        this.changeDetectorRef.detectChanges();

        return error;
    }

    ngOnInit(): void {
        if(this.isAdyen) this.controllers.creditCards.initAdyen();
        if(this.isStripe) this.controllers.creditCards.initStripe();

        this.adyenConfigState$ = this.controllers.creditCards.getAdyenConfig$();
        this.stripeConfigState$ = this.controllers.creditCards.getStripeConfig$();
        this.isMakingRequests$ = this.controllers.creditCards.isMakingRequestsMapped$();

        this.formControl = this.formBuilder.group({
            cardNumber: [
                { value: '' }
            ],
            saveCard: [
                { value: this.guest === false, disabled: false }
            ],
            isDefaultPaymentMethod: [
                { value: this.guest === false, disabled: false }
            ],
        });
    }

    ngAfterViewInit(): void {
        this._initProviders();
    }

    ngOnDestroy(): void {
        this.controllers.creditCards.resetCreditCardErrors();
        this.controllers.payment.destroyAdyenForm();
    }

    public handleFormSubmit($event: Event): void {
        $event.preventDefault();
    }

    public handleOnSubmit($event: Event): void {
        this._staticLoader = 'loading';

        const finalize = () => {
            this.controllers.creditCards.hideAddCardForm();
            this.onFormSubmit.emit({ event: $event, form: null });

            this._staticLoader = null;

            if(!this.modal) {
                Utils.Scroll.smoothScrollTo(0);
            }
        };

        if(this.isAdyen) {
            this.controllers.creditCards.addCreditCard({
                cardNumber: this.formControl.controls.cardNumber.value,
                expiryDate: null,
                saveCard: this.formControl.get('saveCard').value,
                isDefaultPaymentMethod: this.formControl.get('isDefaultPaymentMethod').value,
                adyenPaymentData: this._adyenEncryptedPaymentData
            });

            finalize();
        }

        if(this.isStripe) {
            this.controllers.creditCards.getStripeMemberBillingDetails$()
                .pipe(
                    take(1)
                )
                .subscribe(billing_details => {
                    this._stripe.createPaymentMethod({
                        type: 'card',
                        card: this._stripeFieldsRegistry.card,
                        billing_details
                    }).then((result) => {

                        if(result.error) {
                            this._staticLoader = null;

                            return;
                        }

                        this.controllers.creditCards.addCreditCard({
                            cardNumber: result.paymentMethod.card && (result.paymentMethod.card.last4),
                            expiryDate: result.paymentMethod.card && (result.paymentMethod.card.exp_month + '/' + result.paymentMethod.card.exp_year),
                            saveCard: this.formControl.get('saveCard').value,
                            isDefaultPaymentMethod: this.formControl.get('isDefaultPaymentMethod').value,
                            stripePaymentData: StripePaymentProviderMapper.mapStripePaymentMethodResponse(result.paymentMethod),
                        });

                        finalize();
                    });
                });

        }
    }

    public get isLoading$(): Observable<OLO.Components.LOADING_STATUS> {
        if(this._staticLoader) {
            return of(this._staticLoader);
        }

        return this.isMakingRequests$;
    }

    public get isValid(): boolean {
        return this._isValid;
    }

    public get isAdyen(): boolean {
        return this.config.paymentProvider === OLO.Enums.PAYMENT_PROVIDER.ADYEN;
    }

    public get isStripe(): boolean {
        return this.config.paymentProvider === OLO.Enums.PAYMENT_PROVIDER.STRIPE;
    }

    public get isFormHidden$(): Observable<boolean> {
        if(this.isAdyen) {
            return this.adyenConfigState$
                .pipe(
                    map(config => config.hasSucceeded === false)
                );
        }
        if(this.isStripe) {
            return this.stripeConfigState$
                .pipe(
                    map(config => config.hasSucceeded === false)
                );
        }
    }
}
